/**
 * The floor plan.
 *
 * A waiter reads this to answer two questions: which tables are free, and how
 * long the occupied ones have been sitting. So the tile shows the bill and the
 * elapsed time rather than a status word - "45m, Rs. 3,200" tells a manager
 * more than "OCCUPIED" does.
 *
 * Tapping an occupied table opens its bill on the till; tapping a free one
 * starts an order against it.
 */

import { useCallback, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { FiRefreshCw, FiGrid, FiUsers, FiClock } from 'react-icons/fi';
import toast from 'react-hot-toast';

import { tableApi, orderApi } from '../services/endpoints';
import useAsync from '../hooks/useAsync';
import { PageHeader, EmptyState, StatCard } from '../components/ui/Feedback';
import { SpinnerBlock } from '../components/ui/Spinner';
import { loadFromOrder, clearCart, setTable, setOrderType } from '../store/cartSlice';
import { money, duration } from '../utils/format';

const STATUS_STYLES = {
    FREE: 'border-emerald-300 bg-emerald-50 hover:border-emerald-500',
    OCCUPIED: 'border-blue-300 bg-blue-50 hover:border-blue-500',
    BILLED: 'border-purple-300 bg-purple-50 hover:border-purple-500',
    RESERVED: 'border-amber-300 bg-amber-50 hover:border-amber-500',
    CLEANING: 'border-slate-300 bg-slate-100 hover:border-slate-400'
};

const TablesPage = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const load = useCallback(() => tableApi.list(), []);
    const { data: tables, loading, refetch } = useAsync(load, []);

    // The floor changes without this screen doing anything, so it refreshes
    // itself. Thirty seconds is often enough to be useful without being a
    // distraction on a wall display.
    useEffect(() => {
        const timer = setInterval(refetch, 30000);
        return () => clearInterval(timer);
    }, [refetch]);

    const openTable = async (table) => {
        try {
            if (table.Current_Order_ID) {
                const detail = await orderApi.get(table.Current_Order_ID);
                dispatch(loadFromOrder(detail));
            } else {
                dispatch(clearCart());
                dispatch(setOrderType('DINE_IN'));
                dispatch(setTable({ tableId: table.Table_ID, tableNumber: table.Table_Number }));
            }
            navigate('/pos');
        } catch (err) {
            toast.error(err.message);
        }
    };

    if (loading && !tables) return <SpinnerBlock label="Loading the floor…" />;

    const areas = [...new Set((tables || []).map((t) => t.Area_Name || 'Main'))];
    const free = (tables || []).filter((t) => t.Table_Status === 'FREE').length;
    const occupied = (tables || []).filter((t) => t.Table_Status === 'OCCUPIED').length;
    const openValue = (tables || []).reduce((total, t) => total + (Number(t.Total_Payable) || 0), 0);

    return (
        <div className="page-scroll">
            <PageHeader
                title="Tables"
                subtitle="The floor, live"
                actions={(
                    <button type="button" className="btn-secondary" onClick={() => refetch()}>
                        <FiRefreshCw className="h-4 w-4" /> Refresh
                    </button>
                )}
            />

            <div className="mt-2 grid grid-cols-2 gap-2 sm:grid-cols-3 sm:gap-3">
                <StatCard label="Free" value={free} icon={FiGrid} tone="green" />
                <StatCard label="Occupied" value={occupied} icon={FiUsers} tone="blue" />
                <StatCard
                    label="Open bills"
                    value={`Rs. ${money(openValue)}`}
                    sub="Sitting on tables, not yet paid"
                    icon={FiClock}
                    tone="amber"
                />
            </div>

            {!tables?.length ? (
                <EmptyState
                    className="mt-6"
                    icon={FiGrid}
                    title="No tables have been set up"
                    description="Add them to DINING_TABLE, or run the demo seed script."
                />
            ) : (
                areas.map((area) => (
                    <section key={area} className="mt-2">
                        <h2 className="mb-2 font-semibold text-slate-700">{area}</h2>

                        <div className="grid grid-cols-3 gap-2 xs:grid-cols-4 sm:gap-3 md:grid-cols-5 lg:grid-cols-6 xl:grid-cols-8">
                            {tables
                                .filter((table) => (table.Area_Name || 'Main') === area)
                                .map((table) => (
                                    <button
                                        key={table.Table_ID}
                                        type="button"
                                        onClick={() => openTable(table)}
                                        className={`flex flex-col items-center justify-center gap-1 rounded-xl border-2 p-2.5
                                                    transition-colors sm:p-4
                                                    ${STATUS_STYLES[table.Table_Status] || STATUS_STYLES.FREE}`}
                                    >
                                        <span className="text-xl font-bold text-slate-800 sm:text-2xl">
                                            {table.Table_Number}
                                        </span>
                                        <span className="text-[11px] text-slate-500">
                                            {table.Seating_Capacity} seats
                                        </span>

                                        {table.Current_Order_ID ? (
                                            <>
                                                <span className="tabular text-sm font-semibold text-slate-800">
                                                    {money(table.Total_Payable)}
                                                </span>
                                                <span className="text-[11px] text-slate-500">
                                                    {duration(table.Occupied_Minutes)}
                                                    {table.Waiter_Name && ` · ${table.Waiter_Name.split(' ')[0]}`}
                                                </span>
                                            </>
                                        ) : (
                                            <span className="text-xs font-medium text-emerald-700">Free</span>
                                        )}
                                    </button>
                                ))}
                        </div>
                    </section>
                ))
            )}
        </div>
    );
};

export default TablesPage;
